import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'shared/lib/redux';
import { useGetRecentlyVisitedLocationsQuery } from 'shared/api/recently-visited-svc';
import { Spinner } from 'shared/ui/spinner';
import { Carousel } from 'shared/ui/carousel';

import { getRecentlyVisitedLocations } from '../constants';

import { RecentlyVisitedLocationsLayout } from './recently-visited-locations-layout';
import { RecentlyVisitedLocationsCard } from './recently-visited-locations-card';
import styles from './styles.module.scss';

interface ComponentProps {
  itemsPerSlide: number;
}

const Component: FC<ComponentProps> = ({ itemsPerSlide }) => {
  const { t } = useTranslation();
  const { limit, carouselId } = getRecentlyVisitedLocations(t);

  const hasError = useAppSelector(
    (state) =>
      state['recently-visited-svc'].queries[
        `getRecentlyVisitedLocations(${limit})`
      ]?.status === 'rejected',
  );

  const { data: recentlyVisitedLocations, isLoading } =
    useGetRecentlyVisitedLocationsQuery(limit, {
      skip: hasError,
    });

  if (hasError) {
    return null;
  }

  if (isLoading || !recentlyVisitedLocations) {
    return (
      <RecentlyVisitedLocationsLayout>
        <Spinner
          isCentered={false}
          isDark
          isLarge
          classNames="mx-auto my-5"
          testId="recently-visited-locations-spinner"
        />
      </RecentlyVisitedLocationsLayout>
    );
  }

  if (!recentlyVisitedLocations.length) {
    return null;
  }

  const recentlyVisitedLocationsCards = recentlyVisitedLocations.map(
    (location, index) => {
      const itemKey = `${location.locationId}-${index}`;
      const widthInPercents = Math.floor(100 / itemsPerSlide - 1);

      return (
        <RecentlyVisitedLocationsCard
          key={itemKey}
          location={location}
          widthInPercents={widthInPercents}
        />
      );
    },
  );

  const shouldShowWithCarousel =
    recentlyVisitedLocations.length > itemsPerSlide;

  if (shouldShowWithCarousel) {
    return (
      <RecentlyVisitedLocationsLayout>
        <Carousel
          arrowsContainerId={carouselId}
          itemsPerSlide={itemsPerSlide}
          slideClassNames={styles.carouselSlide}
          testId={carouselId}
          isCycled
        >
          {recentlyVisitedLocationsCards}
        </Carousel>
      </RecentlyVisitedLocationsLayout>
    );
  }

  return (
    <RecentlyVisitedLocationsLayout>
      <ul className={styles.locationsList}>{recentlyVisitedLocationsCards}</ul>
    </RecentlyVisitedLocationsLayout>
  );
};

export const RecentlyVisitedLocations = memo(Component);
